import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@api-ui-app/src/app/app.state';
import {
  addProductTemplateToStore,
  addProductTemplateToStoreFailure,
  addProductTemplateToStoreSuccess,
  createProductTemplate,
  createProductTemplateFailure,
  createProductTemplateSuccess,
  deleteProductTemplate,
  deleteProductTemplateFailure,
  deleteProductTemplateSuccess,
  duplicateProductTemplate,
  duplicateProductTemplateFailure,
  duplicateProductTemplateSuccess,
  editProductTemplate,
  editProductTemplateFailure,
  editProductTemplateSuccess,
  load,
  loadMore,
  loadStart,
  loadSuccess,
  logProductTemplateUsageCompletedEvent,
  logProductTemplateUsageInitiatedEvent,
  refresh,
  renameProductTemplate,
  renameProductTemplateFailure,
  renameProductTemplateSuccess,
  setSearchParams,
  setSortingParams,
  setTemplateName,
} from '@api-ui-app/src/app/e-commerce-product-templates/+state/e-commerce-product-templates.actions';
import { initialState } from '@api-ui-app/src/app/e-commerce-product-templates/+state/e-commerce-product-templates.reducer';
import { productTemplatesSearchLimit } from '@api-ui-app/src/app/e-commerce-product-templates/constants/product-templates-search-limit';
import { ECommerceProductTemplatesFacade } from '@api-ui-app/src/app/e-commerce-product-templates/e-commerce-product-templates.facade';
import { ECommerceProductTemplatesGraphQlService } from '@api-ui-app/src/app/e-commerce-product-templates/services/e-commerce-product-templates-graph-ql.service';
import { EProductTemplateEventName } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-event-name.enum';
import { EProductTemplatesListItem } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-list-item';
import { ECommerceProductWizardInitializationRouteService } from '@api-ui-app/src/app/e-commerce-stores/services/e-commerce-product-wizard/e-commerce-product-wizard-initialization-route.service';
import { MINUTES_TO_MILLISECONDS } from '@api-ui-app/src/app/lib/constants';
import { setSelectedStore } from '@api-ui-app/src/app/ngrx/e-commerce-stores.actions';
import { logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { EProductWizardMode } from '@gelato-api-ui/core/e-commerce/e-product-wizard-mode.enum';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';
import { ECommerceProductGraphQlService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-graph-ql.service';
import { ShowGeneralErrorNotification } from '@gelato-api-ui/ui-kit/src/lib/notification/notification.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ECommerceProductTemplatesEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly router: Router,
    private readonly eCommerceProductTemplatesGraphQlService: ECommerceProductTemplatesGraphQlService,
    private readonly eCommerceProductGraphQlService: ECommerceProductGraphQlService,
    private readonly eCommerceProductWizardInitializationRouteService: ECommerceProductWizardInitializationRouteService,
    private readonly facade: ECommerceProductTemplatesFacade,
  ) {}

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(load),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) => {
        const { searchParams, offset } = action;

        return this.eCommerceProductTemplatesGraphQlService
          .fetchProductTemplatesList(
            clientId,
            searchParams?.templateName,
            searchParams?.sortingParams?.order,
            searchParams?.sortingParams?.orderBy,
            productTemplatesSearchLimit,
            offset,
          )
          .pipe(
            catchError(() => {
              this.store$.dispatch(new ShowGeneralErrorNotification());

              return of(null);
            }),
            map((items: EProductTemplatesListItem[]) => loadSuccess({ searchParams, offset, items })),
          );
      }),
    ),
  );

  loadStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadStart),
      map(() =>
        load({
          searchParams: initialState.searchParams,
          offset: 0,
        }),
      ),
    ),
  );

  setSearchParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSearchParams, setTemplateName, setSortingParams),
      map(() => refresh()),
    ),
  );

  loadMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMore),
      withLatestFrom(this.facade.searchParams$, this.facade.itemsCount$),
      filter(([, , count]) => count % productTemplatesSearchLimit === 0),
      map(([, searchParams, count]) => load({ searchParams, offset: count })),
    ),
  );

  refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refresh, duplicateProductTemplateSuccess, renameProductTemplateSuccess, deleteProductTemplateSuccess),
      withLatestFrom(this.facade.searchParams$),
      map(([, searchParams]) => load({ searchParams, offset: 0 })),
    ),
  );

  duplicateProductTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(duplicateProductTemplate),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductGraphQlService.duplicateProductAsTemplate(clientId, action.id, action.templateName),
      ),
      catchError(() => of(null)),
      map((response: EProductWithVariants) =>
        response ? duplicateProductTemplateSuccess() : duplicateProductTemplateFailure(),
      ),
    ),
  );

  renameProductTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(renameProductTemplate),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductTemplatesGraphQlService.renameProductTemplate(clientId, action.id, action.templateName),
      ),
      catchError(() => of(null)),
      map((response: EProductWithVariants) =>
        response ? renameProductTemplateSuccess() : renameProductTemplateFailure(),
      ),
    ),
  );

  deleteProductTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProductTemplate),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductTemplatesGraphQlService.deleteProductTemplate(clientId, action.id),
      ),
      catchError(() => of(null)),
      map((response: EProductWithVariants) =>
        response ? deleteProductTemplateSuccess() : deleteProductTemplateFailure(),
      ),
    ),
  );

  addProductTemplateToStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addProductTemplateToStore),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductGraphQlService
          .duplicateProductAsDraft(clientId, action.productTemplateId, null, action.storeId)
          .pipe(
            catchError(() => of(null)),
            map((draftProduct: EProductWithVariants) =>
              draftProduct
                ? addProductTemplateToStoreSuccess({ payload: draftProduct })
                : addProductTemplateToStoreFailure(),
            ),
          ),
      ),
    ),
  );

  addProductTemplateToStoreSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addProductTemplateToStoreSuccess),
        tap(action => {
          const { storeId, id } = action.payload;

          this.router.navigate([`/stores/${storeId}/products/edit-draft/${id}`]);
        }),
      ),
    { dispatch: false },
  );

  createProductTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProductTemplate),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductTemplatesGraphQlService.fetchProductTemplatesStore(clientId).pipe(
          catchError(() => of(null)),
          map((store: EStore) => (store ? createProductTemplateSuccess({ store }) : createProductTemplateFailure())),
        ),
      ),
    ),
  );

  createProductTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProductTemplateSuccess),
      map(action => setSelectedStore({ payload: action.store })),
      tap(() => {
        this.router.navigate(['/catalogue/stores/current/products/template-wizard']);
      }),
    ),
  );

  editProductTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editProductTemplate),
      withLatestFrom(this.facade.clientId$),
      switchMap(([action, clientId]) =>
        this.eCommerceProductTemplatesGraphQlService.fetchProductTemplatesStore(clientId).pipe(
          catchError(() => of(null)),
          map((store: EStore) =>
            store ? editProductTemplateSuccess({ store, id: action.id }) : editProductTemplateFailure(),
          ),
        ),
      ),
    ),
  );

  editProductTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editProductTemplateSuccess),
        tap(action => {
          this.store$.dispatch(setSelectedStore({ payload: action.store }));
          this.router.navigate(
            this.eCommerceProductWizardInitializationRouteService.getRoute(
              EProductWizardMode.EDIT_PRODUCT_TEMPLATE,
              action?.store?.id,
              action?.id,
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  showGeneralErrorNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        duplicateProductTemplateFailure,
        renameProductTemplateFailure,
        deleteProductTemplateFailure,
        addProductTemplateToStoreFailure,
        createProductTemplateFailure,
        editProductTemplateFailure,
      ),
      withLatestFrom(this.facade.clientId$),
      map(() => new ShowGeneralErrorNotification()),
    ),
  );

  logProductTemplateUsageInitiatedEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logProductTemplateUsageInitiatedEvent),
        withLatestFrom(this.facade.productTemplateUsageStep$, this.facade.storeType$),
        tap(([, productTemplateUsageStep, storeType]) => {
          logEventV2(EProductTemplateEventName.USAGE_INITIATED, {
            step: productTemplateUsageStep,
            ecomPlatform: storeType,
          });
        }),
      ),
    { dispatch: false },
  );

  logProductTemplateUsageCompletedEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logProductTemplateUsageCompletedEvent),
        withLatestFrom(this.facade.productTemplateUsageStep$, this.facade.productTemplateUsageInitiatedAt$),
        tap(([, productTemplateUsageStep, productTemplateUsageInitiatedAt]) => {
          const currentTimestamp = new Date().getTime();
          const productTemplateUsageTime = productTemplateUsageInitiatedAt
            ? (currentTimestamp - productTemplateUsageInitiatedAt) / MINUTES_TO_MILLISECONDS
            : null;

          logEventV2(EProductTemplateEventName.USAGE_COMPLETED, {
            step: productTemplateUsageStep,
            time: productTemplateUsageTime,
          });
        }),
      ),
    { dispatch: false },
  );
}
